<script lang="ts">
  /**
   * Härd-CRUD-vy för Lyktan (S-04-13).
   *
   * Tre tillstånd:
   *  1. **list** (default): tabell över alla härdar med aktivera/inaktivera-
   *     toggle, redigera-knapp och ta-bort-knapp (soft-delete via backend).
   *  2. **create**: kart-baserad ritning + form för namn, prioritet och
   *     aktiveringsfönster. Återanvänder `MapView` + `HearthDrawing` samt
   *     `buildPolylines` för att producera WKT till backend.
   *  3. **edit**: pre-fyllt form. Geometri är read-only post-create — UI:t
   *     visar varning. Bara namn, prioritet och aktiveringsfönster ändras.
   *
   * Hard-rules:
   *  - Inga fritext-fält. Inga `description`/`comment`/`note`-inputs.
   *    Namn är max 100 tecken och valideras både klient- och serverside.
   *  - Soft-delete via `POST /api/admin/hearths/{id}/deactivate`. Aldrig
   *    hård-DELETE — backend skriver audit-rad i `admin_actions`.
   *  - Cookie-baserad auth + CSRF-header på muterande anrop. Sessionen
   *    lever bara i HttpOnly-cookien (klient-state försvinner vid reload).
   *  - Användartexter på svenska. Vi *inaktiverar* härdar — vi *släcker*
   *    dem inte (UX-disciplin från CLAUDE.md).
   *  - Designspråket matchar pitch-presentationen: varma guld-toner,
   *    glöd-gradient på härd-visualisering, eyebrow-rubriker med stor
   *    letter-spacing.
   */
  import { onDestroy, onMount } from 'svelte';
  import type { Map as MapLibreMap } from 'maplibre-gl';
  import HearthDrawing from '../HearthDrawing.svelte';
  import HearthViewModal from './HearthViewModal.svelte';
  import SnapPreviewMap from './SnapPreviewMap.svelte';
  import MapView from '../MapView.svelte';
  import {
    activateHearth,
    createHearth,
    deactivateHearth,
    isActive,
    listHearths,
    priorityLabel,
    updateHearth,
    type HearthPriority,
    type HearthRecord,
  } from './hearthsClient';
  import {
    buildPolylines,
    createInitialDrawingState,
    type DrawingState,
  } from '../draw/drawingState';
  import { fetchRoute, type BatchProgress } from '../draw/routeService';
  import type { Coordinate, Polyline } from '../draw/types';
  import { formatDate } from '../util/format';
  import {
    ROUTE_DISPLAY_COLORS,
    asRouteDisplayColor,
    hexForColor,
    labelForColor,
    renderColorFor,
  } from '../util/colorPalette';

  type Props = {
    onUnauthorized?: () => void;
    /**
     * Admin-konfigurerade karta-defaults från S-04-22 app_settings.
     * Vidarebefordras till MapView i create-mode. Faller tillbaka på
     * kompilerings-default (Hisingsbacka centrum) om inte angivet.
     */
    initialCenter?: { lng: number; lat: number };
    initialZoom?: number;
    /**
     * Signal-counter från RitaView (UX-fix 2026-05-05): `+` och Refresh
     * är lyfta till Rita-layer-toolbar-raden. Vid klick ökas respektive
     * counter, och vi lyssnar via $effect och kallar `startCreate` /
     * `loadRecords`. Initialvärde 0 ska INTE auto-trigga create —
     * lastSeen-räknarna nedan startar på samma värde och hoppar över
     * matching.
     */
    createTrigger?: number;
    refreshTrigger?: number;
  };
  const {
    onUnauthorized,
    initialCenter,
    initialZoom,
    createTrigger = 0,
    refreshTrigger = 0,
  }: Props = $props();

  // lastSeen-räknare. Initialiseras vid första effect-körning så vi inte
  // auto-triggar create/refresh på initial-render. Måste sättas i $effect
  // för att Svelte 5 ska se prop-värdet (state_referenced_locally).
  let lastCreateTrigger: number | undefined = undefined;
  let lastRefreshTrigger: number | undefined = undefined;

  $effect(() => {
    if (lastCreateTrigger === undefined) {
      lastCreateTrigger = createTrigger;
      return;
    }
    if (createTrigger !== lastCreateTrigger) {
      lastCreateTrigger = createTrigger;
      startCreate();
    }
  });

  $effect(() => {
    if (lastRefreshTrigger === undefined) {
      lastRefreshTrigger = refreshTrigger;
      return;
    }
    if (refreshTrigger !== lastRefreshTrigger) {
      lastRefreshTrigger = refreshTrigger;
      void loadRecords();
    }
  });

  type Mode = 'list' | 'create' | 'edit' | 'preview';

  let mode: Mode = $state('list');
  let records: HearthRecord[] = $state([]);
  let isLoading = $state(true);
  let listErrorMessage: string | null = $state(null);

  let selectedRecord: HearthRecord | null = $state(null);
  let formName: string = $state('');
  let formPriority: HearthPriority = $state('medium');
  let formActiveFrom: string = $state(''); // HTML datetime-local-format eller tom = "alltid"
  let formActiveTo: string = $state('');
  // S-04-24b: admin-valbar grundfärg per runda. NULL = priority-baserad
  // klient-side fallback (samma som backend default vid skapa).
  let formDisplayColor: string | null = $state(null);
  let formError: string | null = $state(null);
  let isSaving = $state(false);

  // Förmedlas till HearthDrawing när kart-vyn är aktiv. Vi lyfter ritningen
  // från komponenten för att kunna läsa polylines vid spara — men håller
  // ritytan presentational i HearthDrawing.
  let mapInstance: MapLibreMap | null = $state(null);
  let drawingState: DrawingState = $state(createInitialDrawingState());

  // Soft-delete-bekräftelse — visas som modal innan vi skickar deactivate.
  let confirmDeactivateId: string | null = $state(null);
  let isMutating = $state(false);
  let rowErrorMessage: string | null = $state(null);

  // S-04-25 visningsläge: när admin klickar på rundans namn i listan
  // öppnas en read-only modal med map-preview + metadata. 'Redigera'-
  // knappen i modalen triggar samma flöde som befintliga 'Redigera'-
  // knappen i listraden — vi delegerar till startEdit().
  let viewedRecord: HearthRecord | null = $state(null);

  function openView(record: HearthRecord): void {
    viewedRecord = record;
  }

  function closeView(): void {
    viewedRecord = null;
  }

  function handleViewEdit(record: HearthRecord): void {
    viewedRecord = null;
    startEdit(record);
  }

  // S-04-26 snap-bekräftelse: efter spara visas en banner i list-mode
  // som beskriver vad som hände med snap-to-road-anropet — adresserar
  // Jonas-feedback "vet inte om snappen funkade" från live-test 2026-05-05.
  // 'snapped'  — GraphHopper-snap lyckades, koord-räknare visas
  // 'fallback' — GraphHopper failade, vi sparade raka linjer mellan klick
  // 'free-mode' — fri ritning, ingen snap-attempt
  type SnapToastKind = 'snapped' | 'fallback' | 'free-mode';
  let snapToast: { kind: SnapToastKind; message: string } | null = $state(null);
  let toastTimerId: number | null = null;

  function showSnapToast(toast: { kind: SnapToastKind; message: string }): void {
    if (toastTimerId !== null) {
      window.clearTimeout(toastTimerId);
    }
    snapToast = toast;
    // Fallback-state hålls längre (8s) eftersom det är degraderingssignal
    // som admin behöver hinna läsa. Success-state räcker 5s.
    const ttl = toast.kind === 'fallback' ? 8000 : 5000;
    toastTimerId = window.setTimeout(() => {
      snapToast = null;
      toastTimerId = null;
    }, ttl);
  }

  function dismissSnapToast(): void {
    snapToast = null;
    if (toastTimerId !== null) {
      window.clearTimeout(toastTimerId);
      toastTimerId = null;
    }
  }

  function countCoords(lines: ReadonlyArray<Polyline>): number {
    return lines.reduce((sum, line) => sum + line.points.length, 0);
  }

  onDestroy(() => {
    if (toastTimerId !== null) {
      window.clearTimeout(toastTimerId);
      toastTimerId = null;
    }
  });

  // S-04-28: iterativ snap-preview-mode. Admin trycker 'Förhandsvisa
  // snap' istället för att direkt spara. Resultatet visas på en read-
  // only-karta, och admin kan iterera ('Försök igen') eller bekräfta.
  // Geometri immutable post-create per UI-kontrakt § 2 — iterationen
  // sker FÖRE backend-save, inte efter.
  // Throttle 5s mellan snap-försök skyddar mot S-04-05b free-tier-incident.
  type SnapPreview = {
    /** Waypoints som användes vid första preview-anropet. */
    waypoints: Coordinate[];
    /** Polyline att spara — antingen snappad eller fallback raka linjer. */
    lines: Polyline[];
    /** Resultatet av senaste snap-försöket. */
    resultKind: 'snapped' | 'fallback';
    /** User-message vid fallback. */
    resultMessage?: string;
    preSnapWaypointCount: number;
    postSnapCoordCount: number;
  };

  let snapPreview: SnapPreview | null = $state(null);
  let isPreviewSnapping: boolean = $state(false);
  let lastPreviewSnapAt: number = 0;
  const PREVIEW_THROTTLE_MS = 5000;

  // Progress under batchade snap-anrop (>5 waypoints). null när snap
  // inte pågår eller är ett singel-anrop. UI visar "Snappar punkt X av
  // Y" så admin ser att en lång runda är på gång (1500ms/batch ger ~12s
  // för en 30-punkts-runda — utan progress känns det som hängning).
  let snapProgress: BatchProgress | null = $state(null);
  function trackSnap(p: BatchProgress): void {
    snapProgress = p;
  }

  async function handlePreviewSnap(): Promise<void> {
    formError = null;
    const validation = validateForm();
    if (validation) {
      formError = validation;
      return;
    }
    const lines = buildPolylines(drawingState);
    if (lines.length === 0) {
      formError = 'Rita rundans geometri på kartan innan du sparar.';
      return;
    }
    // Free-mode eller för få waypoints — ingen snap att förhandsvisa,
    // gå direkt till save (gammalt flöde).
    if (drawingState.mode !== 'snap-to-road' || drawingState.waypoints.length < 2) {
      await handleSubmit();
      return;
    }
    isPreviewSnapping = true;
    snapProgress = null;
    const preCount = drawingState.waypoints.length;
    const result = await fetchRoute(drawingState.waypoints, { onProgress: trackSnap });
    snapProgress = null;
    isPreviewSnapping = false;
    lastPreviewSnapAt = performance.now();
    if (!result.ok && result.cause === 'unauthorized') {
      formError = result.userMessage;
      onUnauthorized?.();
      return;
    }
    const previewLines: Polyline[] = result.ok ? [result.polyline] : lines;
    snapPreview = {
      waypoints: [...drawingState.waypoints],
      lines: previewLines,
      resultKind: result.ok ? 'snapped' : 'fallback',
      resultMessage: result.ok ? undefined : result.userMessage,
      preSnapWaypointCount: preCount,
      postSnapCoordCount: countCoords(previewLines),
    };
    mode = 'preview';
  }

  async function handleRetrySnap(): Promise<void> {
    if (!snapPreview || isPreviewSnapping) return;
    const elapsed = performance.now() - lastPreviewSnapAt;
    if (elapsed < PREVIEW_THROTTLE_MS) {
      const wait = Math.ceil((PREVIEW_THROTTLE_MS - elapsed) / 1000);
      formError = `Vänta ${wait} s mellan snap-försök (skyddar mot rate-limit).`;
      return;
    }
    formError = null;
    isPreviewSnapping = true;
    snapProgress = null;
    const result = await fetchRoute(snapPreview.waypoints, { onProgress: trackSnap });
    snapProgress = null;
    isPreviewSnapping = false;
    lastPreviewSnapAt = performance.now();
    if (!result.ok && result.cause === 'unauthorized') {
      formError = result.userMessage;
      onUnauthorized?.();
      return;
    }
    if (result.ok) {
      snapPreview = {
        ...snapPreview,
        lines: [result.polyline],
        resultKind: 'snapped',
        resultMessage: undefined,
        postSnapCoordCount: result.polyline.points.length,
      };
    } else {
      // Behåll lines som de var — fallback uppdaterar bara message.
      snapPreview = {
        ...snapPreview,
        resultKind: 'fallback',
        resultMessage: result.userMessage,
      };
    }
  }

  async function handleConfirmSave(): Promise<void> {
    if (!snapPreview) return;
    formError = null;
    isSaving = true;
    const wkt = polylinesToWkt(snapPreview.lines);
    const activeFromIso = localInputToIso(formActiveFrom);
    const activeToIso = localInputToIso(formActiveTo);
    // Spara snap-info så vi kan visa korrekt toast efter clearing.
    const finalKind = snapPreview.resultKind;
    const finalMessage = snapPreview.resultMessage;
    const finalPre = snapPreview.preSnapWaypointCount;
    const finalPost = snapPreview.postSnapCoordCount;
    try {
      const result = await createHearth({
        name: formName.trim(),
        geometryWkt: wkt,
        priority: formPriority,
        activeFrom: activeFromIso,
        activeTo: activeToIso,
        displayColor: formDisplayColor,
      });
      if (result.kind === 'ok') {
        mode = 'list';
        drawingState = createInitialDrawingState();
        snapPreview = null;
        await loadRecords();
        const message =
          finalKind === 'snapped'
            ? `Snap till väg lyckades. Rundan följer vägnätet (${finalPre} klick → ${finalPost} koordinater).`
            : `Sparade med raka linjer mellan klick.${finalMessage ? ' ' + finalMessage : ''}`;
        showSnapToast({ kind: finalKind, message });
      } else if (result.kind === 'unauthorized') {
        formError = 'Sessionen har gått ut. Logga in igen.';
        onUnauthorized?.();
      } else if (result.kind === 'not_found') {
        formError = 'Rundan kunde inte hittas.';
      } else {
        formError = result.message;
      }
    } finally {
      isSaving = false;
    }
  }

  function handleAbortPreview(): void {
    mode = 'create';
    snapPreview = null;
    formError = null;
    // drawingState bevaras så admin kan fortsätta justera waypoints
  }

  /**
   * S-04-28 Del 2: klick på en waypoint-cirkel i preview-kartan
   * tar bort den waypointen från listan. Snap kör automatiskt
   * igen så admin ser direkt hur det blir utan punkten. Throttle
   * tillämpas precis som handleRetrySnap.
   *
   * Edge case: om vi går under 2 waypoints faller vi tillbaka
   * till raka linjer (om en finns kvar) eller låter snapPreview
   * vara med ny tom lines-array. Admin avbryt → tillbaka till
   * create-mode med original-state.
   */
  async function handleWaypointRemove(index: number): Promise<void> {
    if (!snapPreview) return;
    if (index < 0 || index >= snapPreview.waypoints.length) return;
    const newWaypoints = snapPreview.waypoints.filter((_, i) => i !== index);
    snapPreview = {
      ...snapPreview,
      waypoints: newWaypoints,
      preSnapWaypointCount: newWaypoints.length,
    };
    if (newWaypoints.length < 2) {
      // För få waypoints för snap — visa info men behåll preview-mode
      // så admin kan lägga till nya punkter.
      snapPreview = {
        ...snapPreview,
        lines: [],
        resultKind: 'fallback',
        resultMessage: 'Minst 2 punkter behövs för snap. Klicka på kartan för att lägga till.',
        postSnapCoordCount: 0,
      };
      return;
    }
    await reSnapWithCurrentWaypoints();
  }

  /**
   * S-04-28 Del 2: klick på kartan utanför en waypoint lägger till
   * en ny waypoint i slutet av listan. Auto-snap körs.
   *
   * Vidare iteration kan beräkna närmsta linje-segment och insert
   * waypoint där — för MVP räcker append-i-slutet, admin kan ta
   * bort fel-placerade och prova igen.
   */
  async function handleWaypointAdd(coord: Coordinate): Promise<void> {
    if (!snapPreview) return;
    const newWaypoints = [...snapPreview.waypoints, coord];
    snapPreview = {
      ...snapPreview,
      waypoints: newWaypoints,
      preSnapWaypointCount: newWaypoints.length,
    };
    if (newWaypoints.length < 2) return;
    await reSnapWithCurrentWaypoints();
  }

  async function reSnapWithCurrentWaypoints(): Promise<void> {
    if (!snapPreview) return;
    const elapsed = performance.now() - lastPreviewSnapAt;
    if (elapsed < PREVIEW_THROTTLE_MS) {
      const wait = Math.ceil((PREVIEW_THROTTLE_MS - elapsed) / 1000);
      formError = `Vänta ${wait} s mellan snap-försök (skyddar mot rate-limit).`;
      return;
    }
    formError = null;
    isPreviewSnapping = true;
    snapProgress = null;
    const result = await fetchRoute(snapPreview.waypoints, { onProgress: trackSnap });
    snapProgress = null;
    isPreviewSnapping = false;
    lastPreviewSnapAt = performance.now();
    if (!result.ok && result.cause === 'unauthorized') {
      formError = result.userMessage;
      onUnauthorized?.();
      return;
    }
    if (result.ok) {
      snapPreview = {
        ...snapPreview,
        lines: [result.polyline],
        resultKind: 'snapped',
        resultMessage: undefined,
        postSnapCoordCount: result.polyline.points.length,
      };
    } else {
      // Bygg raka linjer från waypoints så fallback har något att visa.
      const fallbackLine: Polyline = { points: [...snapPreview.waypoints] };
      snapPreview = {
        ...snapPreview,
        lines: [fallbackLine],
        resultKind: 'fallback',
        resultMessage: result.userMessage,
        postSnapCoordCount: snapPreview.waypoints.length,
      };
    }
  }

  async function loadRecords(): Promise<void> {
    isLoading = true;
    listErrorMessage = null;
    const result = await listHearths();
    if (result.kind === 'ok') {
      records = result.items;
    } else if (result.kind === 'unauthorized') {
      records = [];
      listErrorMessage = 'Sessionen har gått ut. Logga in igen.';
      onUnauthorized?.();
    } else {
      records = [];
      listErrorMessage = result.message;
    }
    isLoading = false;
  }

  onMount(() => {
    void loadRecords();
  });

  function startCreate(): void {
    mode = 'create';
    selectedRecord = null;
    formName = '';
    formPriority = 'medium';
    formActiveFrom = '';
    formActiveTo = '';
    formDisplayColor = null;
    formError = null;
    drawingState = createInitialDrawingState();
  }

  function startEdit(record: HearthRecord): void {
    mode = 'edit';
    selectedRecord = record;
    formName = record.name;
    formPriority = record.priority;
    formActiveFrom = isoToLocalInput(record.activeFrom);
    formActiveTo = record.activeTo ? isoToLocalInput(record.activeTo) : '';
    formDisplayColor = record.displayColor;
    formError = null;
  }

  function cancelEditor(): void {
    mode = 'list';
    selectedRecord = null;
    formDisplayColor = null;
    formError = null;
    drawingState = createInitialDrawingState();
  }

  function selectDisplayColor(color: string | null): void {
    formDisplayColor = color === null ? null : asRouteDisplayColor(color);
  }

  /**
   * Konverterar UTC ISO-8601 till `<input type="datetime-local">`-format
   * (`YYYY-MM-DDTHH:MM`). Vi visar svensk lokaltid så admin förstår vad
   * den ser; backend tar emot ISO-8601 igen vid spara.
   */
  function isoToLocalInput(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number): string => n.toString().padStart(2, '0');
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
  }

  function localInputToIso(value: string): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
  }

  /**
   * Bygger WKT MULTILINESTRING från ritningens polylines.
   * Backend förväntar sig SRID 4326 + MULTILINESTRING-typ
   * (V202604270003__hearths.sql). Vi formaterar oss själva för att slippa
   * en extra runtime-deps; precision på 6 decimaler räcker för WGS84
   * (≈ 0.1 m vid Hisings Backas latitud) och håller payload kompakt.
   */
  function polylinesToWkt(lines: ReadonlyArray<Polyline>): string {
    function fmt(c: Coordinate): string {
      return `${c.longitude.toFixed(6)} ${c.latitude.toFixed(6)}`;
    }
    const parts = lines
      .filter((l) => l.points.length >= 2)
      .map((l) => `(${l.points.map(fmt).join(', ')})`);
    return `MULTILINESTRING(${parts.join(', ')})`;
  }

  function validateForm(): string | null {
    const trimmed = formName.trim();
    if (trimmed.length === 0) return 'Ange ett namn för rundan.';
    if (trimmed.length > 100) return 'Namnet får vara högst 100 tecken.';
    if (formActiveFrom && formActiveTo) {
      const from = new Date(formActiveFrom);
      const to = new Date(formActiveTo);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        return 'Ogiltigt aktiveringsfönster.';
      }
      if (to.getTime() <= from.getTime()) {
        return 'Slut måste vara efter start i aktiveringsfönstret.';
      }
    }
    return null;
  }

  async function handleSubmit(): Promise<void> {
    formError = null;
    const validation = validateForm();
    if (validation) {
      formError = validation;
      return;
    }

    if (mode === 'create') {
      let lines = buildPolylines(drawingState);
      if (lines.length === 0) {
        formError = 'Rita rundans geometri på kartan innan du sparar.';
        return;
      }
      // S-04-05b final-snap-modellen (2026-05-05): vid snap-to-road-mode
      // skickar vi alla klickade waypoints till GraphHopper i en enda
      // call för att få en kanonisk gångväg. Det ersätter de raka
      // linjerna som visats under aktiv ritning. Om GraphHopper failar
      // (rate-limit, no-route) faller vi tillbaka till de raka linjerna.
      // Free-mode behåller sin lokala stroke som-den-är.
      //
      // S-04-26: efter spara-success visas en toast som beskriver vad
      // som hände — bygg pendingToast under flödet, visa när
      // createHearth lyckas.
      isSaving = true;
      const preSnapWaypointCount = drawingState.waypoints.length;
      let pendingToast: { kind: SnapToastKind; message: string } | null = null;
      if (drawingState.mode === 'snap-to-road' && drawingState.waypoints.length >= 2) {
        snapProgress = null;
        const snapResult = await fetchRoute(drawingState.waypoints, { onProgress: trackSnap });
        snapProgress = null;
        if (snapResult.ok) {
          lines = [snapResult.polyline];
          pendingToast = {
            kind: 'snapped',
            message: `Snap till väg lyckades. Rundan följer vägnätet (${preSnapWaypointCount} klick → ${snapResult.polyline.points.length} koordinater).`,
          };
        } else if (snapResult.cause === 'unauthorized') {
          isSaving = false;
          formError = snapResult.userMessage;
          onUnauthorized?.();
          return;
        } else {
          // Graceful degrade: spara med raka linjer + visa varning så
          // admin vet att snap inte lyckades.
          formError = `${snapResult.userMessage} Sparar med raka linjer mellan klick.`;
          pendingToast = {
            kind: 'fallback',
            message: `Sparade med raka linjer mellan klick — GraphHopper kunde inte snap:a denna runda. ${snapResult.userMessage}`,
          };
        }
      } else if (drawingState.mode === 'free') {
        pendingToast = {
          kind: 'free-mode',
          message: `Sparade fri ritning (${countCoords(lines)} koordinater).`,
        };
      }
      const wkt = polylinesToWkt(lines);
      const activeFromIso = localInputToIso(formActiveFrom);
      const activeToIso = localInputToIso(formActiveTo);

      try {
        const result = await createHearth({
          name: formName.trim(),
          geometryWkt: wkt,
          priority: formPriority,
          activeFrom: activeFromIso,
          activeTo: activeToIso,
          displayColor: formDisplayColor,
        });
        if (result.kind === 'ok') {
          mode = 'list';
          drawingState = createInitialDrawingState();
          await loadRecords();
          if (pendingToast) {
            showSnapToast(pendingToast);
          }
        } else if (result.kind === 'unauthorized') {
          formError = 'Sessionen har gått ut. Logga in igen.';
          onUnauthorized?.();
        } else if (result.kind === 'not_found') {
          formError = 'Rundan kunde inte hittas.';
        } else {
          formError = result.message;
        }
      } finally {
        isSaving = false;
      }
      return;
    }

    if (mode === 'edit' && selectedRecord) {
      const activeFromIso = localInputToIso(formActiveFrom) ?? selectedRecord.activeFrom;
      const activeToIso = localInputToIso(formActiveTo);

      isSaving = true;
      try {
        const result = await updateHearth(selectedRecord.id, {
          name: formName.trim(),
          // Geometri är read-only i UI — skicka oförändrad WKT så backend
          // kan validera fältet utan att vi öppnar för geometri-edit.
          geometryWkt: selectedRecord.geometryWkt,
          priority: formPriority,
          activeFrom: activeFromIso,
          activeTo: activeToIso,
          displayColor: formDisplayColor,
        });
        if (result.kind === 'ok') {
          mode = 'list';
          selectedRecord = null;
          await loadRecords();
        } else if (result.kind === 'unauthorized') {
          formError = 'Sessionen har gått ut. Logga in igen.';
          onUnauthorized?.();
        } else if (result.kind === 'not_found') {
          formError = 'Rundan kunde inte hittas.';
        } else {
          formError = result.message;
        }
      } finally {
        isSaving = false;
      }
    }
  }

  async function toggleActive(record: HearthRecord): Promise<void> {
    if (isMutating) return;
    rowErrorMessage = null;
    isMutating = true;
    try {
      const result = isActive(record)
        ? await deactivateHearth(record.id)
        : await activateHearth(record.id);
      if (result.kind === 'ok') {
        records = records.map((r) => (r.id === record.id ? result.record : r));
      } else if (result.kind === 'unauthorized') {
        rowErrorMessage = 'Sessionen har gått ut. Logga in igen.';
        onUnauthorized?.();
      } else if (result.kind === 'not_found') {
        rowErrorMessage = 'Rundan kunde inte hittas.';
      } else {
        rowErrorMessage = result.message;
      }
    } finally {
      isMutating = false;
    }
  }

  async function confirmDeactivate(): Promise<void> {
    if (!confirmDeactivateId || isMutating) return;
    rowErrorMessage = null;
    isMutating = true;
    try {
      const result = await deactivateHearth(confirmDeactivateId);
      if (result.kind === 'ok') {
        records = records.map((r) => (r.id === confirmDeactivateId ? result.record : r));
      } else if (result.kind === 'unauthorized') {
        rowErrorMessage = 'Sessionen har gått ut. Logga in igen.';
        onUnauthorized?.();
      } else if (result.kind === 'not_found') {
        rowErrorMessage = 'Rundan kunde inte hittas.';
      } else {
        rowErrorMessage = result.message;
      }
    } finally {
      isMutating = false;
      confirmDeactivateId = null;
    }
  }

  function dismissDeactivateConfirm(): void {
    if (isMutating) return;
    confirmDeactivateId = null;
  }

  function handleMapReady(map: MapLibreMap | null): void {
    mapInstance = map;
  }

  function handleDrawingStateChange(state: DrawingState): void {
    drawingState = state;
  }
</script>

<section class="hearths" aria-label="Rundor">
  {#if mode === 'list'}
    {#if snapToast}
      <div
        class="snap-toast snap-toast-{snapToast.kind}"
        role="status"
        aria-live="polite"
      >
        <span class="snap-toast-message">{snapToast.message}</span>
        <button
          type="button"
          class="snap-toast-close"
          onclick={dismissSnapToast}
          aria-label="Stäng meddelande"
        >
          ×
        </button>
      </div>
    {/if}

    {#if listErrorMessage}
      <div class="alert error" role="alert">{listErrorMessage}</div>
    {/if}
    {#if rowErrorMessage}
      <div class="alert error" role="alert">{rowErrorMessage}</div>
    {/if}

    {#if !isLoading && records.length === 0 && !listErrorMessage}
      <div class="empty">
        <p class="kicker">Inga rundor än.</p>
        <p>Klicka på <strong>Skapa runda</strong> för att rita den första rundan i kvarteret.</p>
      </div>
    {:else if records.length > 0}
      <div class="table-wrap">
        <table class="hearths-table">
          <thead>
            <tr>
              <th scope="col">Runda</th>
              <th scope="col">Status</th>
              <th scope="col">Prioritet</th>
              <th scope="col">Aktiv från</th>
              <th scope="col">Aktiv till</th>
              <th scope="col" class="actions-col">Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {#each records as record (record.id)}
              {@const active = isActive(record)}
              <tr class:inactive={!active}>
                <td class="name-cell">
                  <button
                    type="button"
                    class="name-link"
                    onclick={() => openView(record)}
                    aria-label="Visa rundan {record.name}"
                  >
                    <span class="hearth-glyph" aria-hidden="true" class:dim={!active}>
                      <span class="petal petal-1"></span>
                      <span class="petal petal-2"></span>
                      <span class="petal petal-3"></span>
                      <span class="petal petal-4"></span>
                      <span class="petal petal-5"></span>
                      <span class="petal petal-6"></span>
                      <span class="core"></span>
                    </span>
                    <span
                      class="color-pip"
                      aria-hidden="true"
                      style="background:{renderColorFor(record.displayColor, record.priority)};"
                    ></span>
                    <span class="name-label">{record.name}</span>
                  </button>
                </td>
                <td>
                  {#if active}
                    <span class="status status-active">Aktiv</span>
                  {:else}
                    <span class="status status-inactive">Inaktiv</span>
                  {/if}
                </td>
                <td>
                  <span class="priority priority-{record.priority}">
                    {priorityLabel(record.priority)}
                  </span>
                </td>
                <td class="dim">{formatDate(record.activeFrom)}</td>
                <td class="dim">{formatDate(record.activeTo)}</td>
                <td class="actions-cell">
                  <button
                    type="button"
                    class="icon-btn icon-btn-row icon-btn-secondary"
                    onclick={() => startEdit(record)}
                    disabled={isMutating}
                    aria-label="Redigera {record.name}"
                    title="Redigera"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </button>
                  {#if active}
                    <button
                      type="button"
                      class="icon-btn icon-btn-row icon-btn-danger"
                      onclick={() => (confirmDeactivateId = record.id)}
                      disabled={isMutating}
                      aria-label="Inaktivera {record.name}"
                      title="Inaktivera runda"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                        <line x1="12" y1="2" x2="12" y2="12" />
                      </svg>
                    </button>
                  {:else}
                    <button
                      type="button"
                      class="icon-btn icon-btn-row icon-btn-secondary"
                      onclick={() => toggleActive(record)}
                      disabled={isMutating}
                      aria-label="Aktivera {record.name}"
                      title="Aktivera runda"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                    </button>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  {/if}

  {#if mode === 'create'}
    <div class="editor">
      <div class="editor-header">
        <div class="eyebrow accent">Ny runda</div>
        <h3>Rita ut rundan på kartan</h3>
        <p class="dim">
          Välj snap-till-väg när rundan följer befintliga gångstråk. Växla till fri ritning för
          parker eller platser där OpenStreetMap saknar data.
        </p>
      </div>

      <div class="map-section">
        <div class="drawing-host">
          <HearthDrawing map={mapInstance} onStateChange={handleDrawingStateChange} />
        </div>
        <div class="map-host">
          <MapView onMapReady={handleMapReady} {initialCenter} {initialZoom} />
        </div>
      </div>

      <form
        class="form"
        onsubmit={(e) => {
          e.preventDefault();
          void handlePreviewSnap();
        }}
      >
        <div class="field">
          <label for="hearth-name">Namn</label>
          <input
            id="hearth-name"
            type="text"
            bind:value={formName}
            maxlength="100"
            required
            autocomplete="off"
            placeholder="t.ex. Lekplatsen vid Hisingsparken"
          />
          <p class="field-hint">
            Max 100 tecken. Kategorisk identifierare — ingen fritext om personer eller händelser.
          </p>
        </div>

        <fieldset class="field">
          <legend>Prioritet</legend>
          <div class="radio-group">
            <label class="radio">
              <input type="radio" name="priority" value="low" bind:group={formPriority} />
              <span>Låg</span>
            </label>
            <label class="radio">
              <input type="radio" name="priority" value="medium" bind:group={formPriority} />
              <span>Medel</span>
            </label>
            <label class="radio">
              <input type="radio" name="priority" value="high" bind:group={formPriority} />
              <span>Hög</span>
            </label>
          </div>
        </fieldset>

        <fieldset class="field">
          <legend>Aktiveringsfönster</legend>
          <p class="field-hint">
            Lämna båda tomma för "alltid aktiv". Backend default:ar start till nu om fältet är tomt.
          </p>
          <div class="datetime-row">
            <label>
              <span>Från</span>
              <input type="datetime-local" bind:value={formActiveFrom} />
            </label>
            <label>
              <span>Till</span>
              <input type="datetime-local" bind:value={formActiveTo} />
            </label>
          </div>
        </fieldset>

        {#if formError}
          <div class="alert error" role="alert">{formError}</div>
        {/if}

        <div class="form-actions">
          <button
            type="submit"
            class="btn primary"
            disabled={isSaving || isPreviewSnapping}
            title="Kör snap mot vägnätet och visar resultatet — bekräfta i nästa steg"
          >
            {#if isPreviewSnapping}
              {#if snapProgress && snapProgress.totalBatches > 1}
                Snappar del {snapProgress.batchIndex} av {snapProgress.totalBatches}…
              {:else}
                Förhandsvisar…
              {/if}
            {:else if isSaving}
              {#if snapProgress && snapProgress.totalBatches > 1}
                Snappar del {snapProgress.batchIndex} av {snapProgress.totalBatches}…
              {:else}
                Sparar…
              {/if}
            {:else}
              Förhandsvisa snap
            {/if}
          </button>
          <button
            type="button"
            class="btn secondary"
            onclick={cancelEditor}
            disabled={isSaving || isPreviewSnapping}
          >
            Avbryt
          </button>
        </div>
      </form>
    </div>
  {/if}

  {#if mode === 'edit' && selectedRecord}
    <div class="editor">
      <div class="editor-header">
        <div class="eyebrow accent">Redigera runda</div>
        <h3>{selectedRecord.name}</h3>
      </div>

      <div class="alert info" role="note">
        <strong>Geometri kan inte ändras.</strong> Rundans sträckning sätts vid skapandet och behålls
        oförändrad — det säkerställer att täckningsdata över tid alltid hör till samma fysiska sträcka.
        Behöver du en annan sträckning? Skapa en ny runda och inaktivera den här.
      </div>

      <form
        class="form"
        onsubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <div class="field">
          <label for="hearth-name-edit">Namn</label>
          <input
            id="hearth-name-edit"
            type="text"
            bind:value={formName}
            maxlength="100"
            required
            autocomplete="off"
          />
          <p class="field-hint">Max 100 tecken.</p>
        </div>

        <fieldset class="field">
          <legend>Prioritet</legend>
          <div class="radio-group">
            <label class="radio">
              <input type="radio" name="priority-edit" value="low" bind:group={formPriority} />
              <span>Låg</span>
            </label>
            <label class="radio">
              <input type="radio" name="priority-edit" value="medium" bind:group={formPriority} />
              <span>Medel</span>
            </label>
            <label class="radio">
              <input type="radio" name="priority-edit" value="high" bind:group={formPriority} />
              <span>Hög</span>
            </label>
          </div>
        </fieldset>

        <fieldset class="field">
          <legend>Färg på kartan</legend>
          <div class="color-grid">
            <button
              type="button"
              class="color-swatch fallback"
              class:selected={formDisplayColor === null}
              onclick={() => selectDisplayColor(null)}
              aria-label="Prioritetsfärg"
              aria-pressed={formDisplayColor === null}
              title="Prioritetsfärg"
            >
              <span class="swatch-dot fallback-dot" aria-hidden="true"></span>
              <span class="swatch-label">Prio</span>
            </button>
            {#each ROUTE_DISPLAY_COLORS as color (color)}
              <button
                type="button"
                class="color-swatch"
                class:selected={formDisplayColor === color}
                onclick={() => selectDisplayColor(color)}
                aria-label={labelForColor(color)}
                aria-pressed={formDisplayColor === color}
                title={labelForColor(color)}
              >
                <span class="swatch-dot" style="background:{hexForColor(color)};" aria-hidden="true"></span>
                <span class="swatch-label">{labelForColor(color)}</span>
              </button>
            {/each}
          </div>
        </fieldset>

        <fieldset class="field">
          <legend>Aktiveringsfönster</legend>
          <div class="datetime-row">
            <label>
              <span>Från</span>
              <input type="datetime-local" bind:value={formActiveFrom} />
            </label>
            <label>
              <span>Till</span>
              <input type="datetime-local" bind:value={formActiveTo} />
            </label>
          </div>
        </fieldset>

        {#if formError}
          <div class="alert error" role="alert">{formError}</div>
        {/if}

        <div class="form-actions">
          <button type="submit" class="btn primary" disabled={isSaving}>
            {isSaving ? 'Sparar…' : 'Spara ändringar'}
          </button>
          <button type="button" class="btn secondary" onclick={cancelEditor} disabled={isSaving}>
            Avbryt
          </button>
        </div>
      </form>
    </div>
  {/if}

  {#if mode === 'preview' && snapPreview}
    <div class="editor preview-editor">
      <div class="editor-header">
        <div class="eyebrow accent">Förhandsgranskning av snap</div>
        <h3>{formName}</h3>
      </div>

      <div
        class="alert {snapPreview.resultKind === 'snapped' ? 'success' : 'info'}"
        role="status"
        aria-live="polite"
      >
        {#if snapPreview.resultKind === 'snapped'}
          <strong>Snap till väg lyckades.</strong>
          {snapPreview.preSnapWaypointCount} klickade punkter → {snapPreview.postSnapCoordCount}
          koord-punkter längs vägnätet.
        {:else}
          <strong>Snap misslyckades.</strong>
          Visar raka linjer mellan klick. {snapPreview.resultMessage ?? ''}
        {/if}
      </div>

      <p class="field-hint preview-hint">
        Klicka på en numrerad punkt för att <strong>ta bort</strong> den. Klicka på kartan för att
        <strong>lägga till</strong> en ny punkt i slutet. Snap körs om automatiskt efter varje
        ändring (max 1 anrop per 5 sekunder).
      </p>

      <div class="map-host preview-map-host">
        <SnapPreviewMap
          waypoints={snapPreview.waypoints}
          snappedLines={snapPreview.lines}
          priority={formPriority}
          displayColor={formDisplayColor}
          onWaypointRemove={(idx) => void handleWaypointRemove(idx)}
          onWaypointAdd={(coord) => void handleWaypointAdd(coord)}
        />
      </div>

      {#if formError}
        <div class="alert error" role="alert">{formError}</div>
      {/if}

      <div class="form-actions">
        <button
          type="button"
          class="btn primary"
          onclick={() => void handleConfirmSave()}
          disabled={isSaving || isPreviewSnapping || snapPreview.lines.length === 0}
        >
          {isSaving ? 'Sparar…' : 'Bekräfta och spara'}
        </button>
        <button
          type="button"
          class="btn secondary"
          onclick={() => void handleRetrySnap()}
          disabled={isSaving || isPreviewSnapping || snapPreview.waypoints.length < 2}
          title="Kör snap-anropet igen mot exakt samma waypoints"
        >
          {isPreviewSnapping ? 'Snappar…' : 'Försök snap igen'}
        </button>
        <button
          type="button"
          class="btn secondary"
          onclick={handleAbortPreview}
          disabled={isSaving || isPreviewSnapping}
        >
          Avbryt
        </button>
      </div>
    </div>
  {/if}

  {#if viewedRecord}
    <HearthViewModal record={viewedRecord} onClose={closeView} onEdit={handleViewEdit} />
  {/if}

  {#if confirmDeactivateId !== null}
    <div
      class="modal-backdrop"
      role="presentation"
      onclick={dismissDeactivateConfirm}
      onkeydown={(e) => {
        if (e.key === 'Escape') dismissDeactivateConfirm();
      }}
    >
      <div
        class="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deactivate-title"
        tabindex="-1"
        onclick={(e) => e.stopPropagation()}
        onkeydown={(e) => e.stopPropagation()}
      >
        <h3 id="deactivate-title">Inaktivera runda</h3>
        <p>
          Rundan inaktiveras och slutar samla täckning. Aggregat och historik bevaras spårbart i
          plattformen — ingenting raderas i tysthet. Du kan aktivera rundan igen senare.
        </p>
        <div class="modal-actions">
          <button
            type="button"
            class="btn secondary"
            onclick={dismissDeactivateConfirm}
            disabled={isMutating}
          >
            Avbryt
          </button>
          <button
            type="button"
            class="btn danger"
            onclick={confirmDeactivate}
            disabled={isMutating}
          >
            {isMutating ? 'Inaktiverar…' : 'Inaktivera runda'}
          </button>
        </div>
      </div>
    </div>
  {/if}
</section>

<style>
  .hearths {
    display: grid;
    gap: 1rem;
  }

  header {
    margin-bottom: 1.75rem;
  }

  .eyebrow {
    font-size: 0.85rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--accent);
    font-weight: 600;
    margin-bottom: 0.6rem;
  }
  .eyebrow.accent {
    color: var(--glow);
  }

  h2 {
    font-size: 1.9rem;
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: -0.025em;
    margin: 0 0 0.6rem;
  }

  .lead {
    margin: 0;
    color: var(--text-dim);
    max-width: 56em;
  }

  /* .toolbar borttagen (UX-fix 2026-05-05) — `+` och Refresh ägs nu av
     RitaView-layer-toolbar. icon-btn-styles bevaras för row-actions
     (Redigera/Inaktivera) i tabellen. */

  .btn {
    appearance: none;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 0.55rem 1.1rem;
    font: inherit;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition:
      background-color 120ms ease,
      border-color 120ms ease,
      color 120ms ease;
  }
  .btn.small {
    font-size: 0.82rem;
    padding: 0.35rem 0.75rem;
  }
  .btn.primary {
    background: var(--accent);
    color: var(--bg);
    border-color: var(--accent);
  }
  .btn.primary:hover:not(:disabled),
  .btn.primary:focus-visible:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
  }
  .btn.secondary {
    background: transparent;
    color: var(--accent);
    border-color: var(--accent);
  }
  .btn.secondary:hover:not(:disabled),
  .btn.secondary:focus-visible:not(:disabled) {
    background: var(--accent-soft);
    color: var(--glow);
  }
  .btn.danger {
    background: transparent;
    color: var(--danger);
    border-color: var(--danger);
  }
  .btn.danger:hover:not(:disabled),
  .btn.danger:focus-visible:not(:disabled) {
    background: var(--danger-soft);
  }
  .btn:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Ikon-knappar — text-knappar utbytta mot SVG-only enligt Jonas
     UX-feedback 2026-05-05. aria-label + title bevaras för
     accessibility. */
  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text-dim);
    cursor: pointer;
    width: 2.4rem;
    height: 2.4rem;
    padding: 0.45rem;
    font-family: inherit;
    transition: color 120ms ease, border-color 120ms ease, background 120ms ease;
  }
  .icon-btn svg {
    width: 100%;
    height: 100%;
    display: block;
  }
  .icon-btn:hover:not(:disabled) {
    color: var(--text);
    border-color: var(--text-dim);
  }
  .icon-btn:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
  .icon-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .icon-btn-primary {
    color: var(--bg);
    background: var(--accent);
    border-color: var(--accent);
  }
  .icon-btn-primary:hover:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
    color: var(--bg);
  }
  .icon-btn-secondary {
    color: var(--accent);
    border-color: var(--accent);
  }
  .icon-btn-secondary:hover:not(:disabled) {
    background: var(--accent-soft);
    color: var(--glow);
  }
  .icon-btn-danger {
    color: var(--danger);
    border-color: var(--danger);
  }
  .icon-btn-danger:hover:not(:disabled) {
    background: var(--danger-soft);
  }
  .icon-btn-row {
    width: 2rem;
    height: 2rem;
    padding: 0.35rem;
  }
  .spinning {
    animation: icon-spin 900ms linear infinite;
  }
  @keyframes icon-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .alert {
    padding: 0.75rem 1rem;
    border-radius: 4px;
    margin-bottom: 1.25rem;
    font-size: 0.92rem;
    border: 1px solid var(--rule);
  }
  .alert.error {
    color: var(--danger);
    background: var(--danger-soft);
    border-color: var(--danger);
  }
  .alert.info {
    color: var(--text);
    background: var(--accent-soft);
    border-color: var(--accent);
  }

  .snap-toast {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-radius: 4px;
    margin-bottom: 1.25rem;
    font-size: 0.92rem;
    border: 1px solid var(--rule);
  }
  .snap-toast-snapped {
    color: var(--text);
    background: var(--accent-soft);
    border-color: var(--accent);
  }
  .snap-toast-fallback {
    color: var(--text);
    background: transparent;
    border: 1px dashed var(--accent);
  }
  .snap-toast-free-mode {
    color: var(--text-dim);
    background: var(--bg-elev);
    border-color: var(--rule);
  }
  .snap-toast-message {
    flex: 1;
    line-height: 1.4;
  }
  .snap-toast-close {
    background: transparent;
    border: none;
    color: var(--text-dim);
    cursor: pointer;
    font-size: 1.3rem;
    line-height: 1;
    padding: 0 0.25rem;
  }
  .snap-toast-close:hover,
  .snap-toast-close:focus-visible {
    color: var(--text);
    outline: none;
  }
  .snap-toast-close:focus-visible {
    box-shadow: 0 0 0 2px var(--glow);
    border-radius: 2px;
  }

  .empty {
    padding: 1.5rem;
    border: 1px dashed var(--rule);
    border-radius: 4px;
    color: var(--text-dim);
  }
  .kicker {
    font-size: 1.1rem;
    color: var(--text);
    font-weight: 500;
    border-left: 2px solid var(--accent);
    padding-left: 1rem;
    margin: 0 0 0.75rem;
  }

  .table-wrap {
    overflow-x: auto;
    border: 1px solid var(--rule);
    border-radius: 4px;
  }

  .hearths-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.92rem;
  }
  .hearths-table thead {
    background: rgba(212, 160, 86, 0.04);
  }
  .hearths-table th {
    text-align: left;
    padding: 0.7rem 0.85rem;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--text-dim);
    font-weight: 600;
    border-bottom: 1px solid var(--rule);
  }
  .hearths-table td {
    padding: 0.85rem;
    border-bottom: 1px solid var(--rule);
    vertical-align: middle;
  }
  .hearths-table tr:last-child td {
    border-bottom: none;
  }
  .hearths-table tr.inactive td {
    color: var(--text-dim);
  }
  .hearths-table td.dim {
    color: var(--text-dim);
    font-variant-numeric: tabular-nums;
    font-size: 0.85rem;
  }
  .actions-col {
    width: 1%;
    white-space: nowrap;
  }
  .actions-cell {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  .name-cell {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .name-link {
    /* S-04-25: namn-cellen är en knapp som öppnar visnings-modal.
       Visuellt en transparent button — inget extra chrome utöver
       fokus-ring. */
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: transparent;
    border: none;
    padding: 0.2rem 0.4rem;
    margin: -0.2rem -0.4rem;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
    text-align: left;
    color: inherit;
    width: 100%;
  }
  .name-link:hover .name-label,
  .name-link:focus-visible .name-label {
    color: var(--glow);
    text-decoration: underline;
  }
  /* S-04-24b färg-prick framför namnet i listraden så admin direkt
     ser vilken färg rundan har på kartan. */
  .color-pip {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1.5px solid var(--bg);
    box-shadow: 0 0 0 1px var(--rule);
    flex-shrink: 0;
  }
  /* S-04-24b form-swatches för färgval. Display: inline-flex med
     flex-wrap så swatches radas upp horisontellt och bryter till
     ny rad vid behov. */
  .color-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
  .color-swatch {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 0.4rem 0.55rem;
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 4px;
    cursor: pointer;
    font-family: inherit;
    color: var(--text-dim);
    font-size: 0.75rem;
    min-width: 4.5rem;
  }
  .color-swatch:hover:not(.selected) {
    color: var(--text);
    border-color: var(--text-dim);
  }
  .color-swatch.selected {
    border-color: var(--accent);
    background: var(--accent-soft);
    color: var(--glow);
  }
  .color-swatch:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
  .swatch-dot {
    display: inline-block;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 1.5px solid var(--bg);
    box-shadow: 0 0 0 1px var(--rule);
  }
  .fallback-dot {
    background: linear-gradient(
      135deg,
      #c4501a 0%,
      #c4501a 45%,
      #5c5247 55%,
      #5c5247 100%
    );
  }
  .swatch-label {
    line-height: 1;
  }
  .name-link:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
  .name-label {
    color: var(--text);
    font-weight: 600;
  }
  tr.inactive .name-label {
    color: var(--text-dim);
  }

  /*
   * Härd-glyph: blomma med 6 kron-blad + glödande kärna.
   * Inspiration från walk-aktiv.png + walk-summary.png.
   * Aktiv: full opacity, glow-gradient. Inaktiv: dimmad, monokrom.
   */
  .hearth-glyph {
    display: inline-block;
    position: relative;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
  }
  .hearth-glyph .core {
    position: absolute;
    inset: 9px;
    border-radius: 50%;
    background: radial-gradient(circle, var(--glow) 0%, var(--accent) 55%, var(--accent-soft) 100%);
    box-shadow: 0 0 8px rgba(240, 192, 112, 0.5);
  }
  .hearth-glyph .petal {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 6px;
    height: 12px;
    margin-left: -3px;
    margin-top: -14px;
    background: var(--accent);
    border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
    transform-origin: 50% 14px;
    opacity: 0.85;
  }
  .hearth-glyph .petal-1 {
    transform: rotate(0deg);
  }
  .hearth-glyph .petal-2 {
    transform: rotate(60deg);
  }
  .hearth-glyph .petal-3 {
    transform: rotate(120deg);
  }
  .hearth-glyph .petal-4 {
    transform: rotate(180deg);
  }
  .hearth-glyph .petal-5 {
    transform: rotate(240deg);
  }
  .hearth-glyph .petal-6 {
    transform: rotate(300deg);
  }
  .hearth-glyph.dim .petal {
    opacity: 0.4;
    background: var(--text-dim);
  }
  .hearth-glyph.dim .core {
    background: radial-gradient(circle, var(--text-dim) 0%, var(--rule) 70%, transparent 100%);
    box-shadow: none;
    opacity: 0.5;
  }

  .status {
    display: inline-block;
    padding: 0.18rem 0.55rem;
    border-radius: 4px;
    font-size: 0.78rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-weight: 600;
  }
  .status-active {
    background: var(--accent-soft);
    color: var(--glow);
    border: 1px solid var(--accent);
  }
  .status-inactive {
    background: transparent;
    color: var(--text-dim);
    border: 1px solid var(--rule);
  }

  .priority {
    display: inline-block;
    font-size: 0.85rem;
    font-weight: 500;
  }
  .priority-high {
    color: var(--glow);
  }
  .priority-medium {
    color: var(--accent);
  }
  .priority-low {
    color: var(--text-dim);
  }

  /* ---- Editor (create/edit) ---- */
  .editor {
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 1.5rem;
    background: var(--bg);
  }
  .editor-header {
    margin-bottom: 1.25rem;
  }
  .editor-header h3 {
    font-size: 1.4rem;
    font-weight: 700;
    letter-spacing: -0.015em;
    margin: 0 0 0.5rem;
  }
  .editor-header .dim {
    color: var(--text-dim);
    margin: 0;
  }

  .map-section {
    margin-bottom: 1.5rem;
  }
  .drawing-host {
    margin-bottom: 0.75rem;
  }
  .map-host {
    width: 100%;
    height: 60vh;
    min-height: 420px;
    border-radius: 4px;
    overflow: hidden;
    border: 1px solid var(--rule);
  }

  .form {
    display: grid;
    gap: 1.25rem;
  }
  .field {
    display: grid;
    gap: 0.4rem;
    border: none;
    padding: 0;
    margin: 0;
  }
  .field legend,
  .field label {
    font-size: 0.85rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-dim);
    font-weight: 600;
  }
  .field-hint {
    margin: 0;
    color: var(--text-dim);
    font-size: 0.85rem;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 400;
  }
  .field input[type='text'],
  .field input[type='datetime-local'] {
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text);
    padding: 0.6rem 0.8rem;
    font: inherit;
    font-size: 0.95rem;
    width: 100%;
    color-scheme: dark;
  }
  .field input[type='text']:focus,
  .field input[type='datetime-local']:focus {
    outline: 2px solid var(--accent);
    outline-offset: 0;
    border-color: var(--accent);
  }

  .radio-group {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
    text-transform: none;
    letter-spacing: normal;
  }
  .radio {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 0.85rem;
    border: 1px solid var(--rule);
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.92rem;
    color: var(--text);
    background: var(--bg-elev);
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
  }
  .radio:has(input:checked) {
    border-color: var(--accent);
    background: var(--accent-soft);
    color: var(--glow);
  }
  .radio input {
    accent-color: var(--accent);
  }

  .datetime-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.85rem;
    margin-top: 0.4rem;
  }
  .datetime-row label {
    display: grid;
    gap: 0.3rem;
  }
  .datetime-row label span {
    font-size: 0.78rem;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 600;
  }

  .form-actions {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
  }

  /* ---- Modal (delete-confirm) ---- */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.65);
    display: grid;
    place-items: center;
    padding: 1rem;
    z-index: 100;
  }
  .modal {
    background: var(--bg-elev);
    border: 1px solid var(--accent);
    border-radius: 4px;
    padding: 1.5rem;
    max-width: 460px;
    width: 100%;
    color: var(--text);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
  }
  .modal h3 {
    margin: 0 0 0.75rem;
    font-size: 1.2rem;
    letter-spacing: -0.01em;
  }
  .modal p {
    margin: 0 0 1.25rem;
    color: var(--text-dim);
  }
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
  }

  @media (max-width: 720px) {
    h2 {
      font-size: 1.5rem;
    }
    .actions-cell {
      justify-content: flex-start;
    }
    .datetime-row {
      grid-template-columns: 1fr;
    }
  }
</style>
